import { useEffect, useState, type FormEvent } from 'react'

import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'

import { DOCUMENT_LIBRARY_COPY } from '#app/routes/mocra+/_form+/components/document-libraries/constants.ts'
import { DocumentLibraryForm } from '#app/routes/mocra+/_form+/components/document-libraries/form.tsx'
import {
	collectDocumentLibraryErrors,
	createDocumentLibraryDraft,
	sanitizeDocumentLibraryDraft,
	type DocumentLibraryDraft,
} from '#app/routes/mocra+/_form+/components/document-libraries/types.ts'

export type DocumentLibraryOverlayMode = 'add' | 'edit'

type DraftErrors = Partial<Record<keyof DocumentLibraryDraft, string>>

type Props = {
	readonly mode: DocumentLibraryOverlayMode
	readonly open: boolean
	readonly onOpenChange: (open: boolean) => void
	readonly onSubmit: (draft: DocumentLibraryDraft) => void
	readonly initialValue?: DocumentLibraryDraft
	readonly onCancel?: () => void
	readonly onClosed?: () => void
}

export function DocumentLibraryOverlay({
	mode,
	open,
	onOpenChange,
	onSubmit,
	initialValue,
	onCancel,
	onClosed,
}: Props) {
	const [draft, setDraft] = useState<DocumentLibraryDraft>(
		initialValue ? { ...initialValue } : createDocumentLibraryDraft(),
	)
	const [errors, setErrors] = useState<DraftErrors>({})

	useEffect(() => {
		if (open) {
			setDraft(
				initialValue ? { ...initialValue } : createDocumentLibraryDraft(),
			)
			setErrors({})
		}
	}, [open, initialValue])

	useEffect(() => {
		if (!open) {
			onClosed?.()
		}
	}, [onClosed, open])

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		event.stopPropagation()
		const sanitized = sanitizeDocumentLibraryDraft(draft)
		const validationErrors = collectDocumentLibraryErrors(sanitized)
		if (validationErrors.length > 0) {
			const nextErrors: DraftErrors = {}
			nextErrors.location = validationErrors[0]
			setErrors(nextErrors)
			return
		}
		onSubmit(sanitized)
	}

	const handleCancel = () => {
		onOpenChange(false)
		onCancel?.()
	}

	const titleCopy =
		mode === 'add'
			? DOCUMENT_LIBRARY_COPY.title.add
			: DOCUMENT_LIBRARY_COPY.title.edit
	const submitCopy =
		mode === 'add'
			? DOCUMENT_LIBRARY_COPY.submit.add
			: DOCUMENT_LIBRARY_COPY.submit.edit

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={onOpenChange}
			title={titleCopy}
			description={DOCUMENT_LIBRARY_COPY.description}
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={handleCancel}>
						Cancel
					</Button>
					<Button type="submit" form="document-library-overlay-form">
						{submitCopy}
					</Button>
				</div>
			}
		>
			<DocumentLibraryForm
				formId="document-library-overlay-form"
				draft={draft}
				errors={errors}
				onDraftFieldChange={(field, value) =>
					setDraft((prev) => ({ ...prev, [field]: value }))
				}
				onSubmit={handleSubmit}
			/>
		</ResponsiveOverlay>
	)
}
